import React, { useEffect, useMemo, useState } from "react"; 
import { Offcanvas, CloseButton, Form } from "react-bootstrap";
import { Button as ButtonAntd, Input, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faEdit } from "@fortawesome/free-solid-svg-icons";
import { ExpandOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";  // ✅ Rich text editor
import "react-quill/dist/quill.snow.css";
import useDialMemberships from "hooks/feature-dial/useDialMemberships";
import useApplications from "hooks/applications/useApplications";
const getAppLabel = (app) =>
  app?.code ||
  app?.appCode ||
  app?.name ||
  app?.displayName ||
  app?.key ||
  app?.appKey ||
  app?.slug ||
  app?.value ||
  app?.id ||
  app?.applicationName ||
  "Unknown App";

const getAppId = (app) =>
  app?.id || app?.appId || app?.applicationId || app?.uuid || app?.key || null;

export default function FeatureDialDrawer({
  dial,
  show,
  onClose,
  onSave,
  isSubmitting
}) {
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [nameError, setNameError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const { getApplicationsForDial } = useDialMemberships();
  const { applications } = useApplications();

  const isNew = !dial;

  const appOptions = useMemo(
    () =>
      (applications || [])
        .map((app) => {
          const appId = getAppId(app);
          const appLabel = getAppLabel(app);
          if (!appId || !appLabel) return null;
          return { value: String(appId), label: String(appLabel) };
        })
        .filter(Boolean),
    [applications]
  );

  const appLabelById = useMemo(() => {
    const map = new Map();
    appOptions.forEach((opt) => {
      map.set(String(opt.value), opt.label);
    });
    return map;
  }, [appOptions]);

  useEffect(() => {
    setVisible(show);
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const base = dial
      ? {
          id: dial.id,
          name: dial.name || "",
          description: dial.description || "",
          appIds: [],
          isOn: !!dial.isOn
        }
      : {
          name: "",
          description: "",
          appIds: [],
          isOn: false
        };

    setForm(base);
    setEditing(isNew);
    setNameError("");
  }, [show, dial, isNew]);

  useEffect(() => {
    if (!show || !dial?.id) return;

    let isMounted = true;

    const loadApps = async () => {
      try {
        const apps = await getApplicationsForDial(dial.id);
        if (!isMounted) return;
        const ids = (apps || [])
          .map((app) => {
            const appId = getAppId(app);
            return appId ? String(appId) : null;
          })
          .filter(Boolean);
        setForm((prev) => ({ ...prev, appIds: ids }));
      } catch (err) {
        // Silent fail; user can still select apps manually.
      }
    };

    loadApps();

    return () => {
      isMounted = false;
    };
  }, [show, dial?.id, getApplicationsForDial]);

  useEffect(() => {
    if (!show || !dial?.oliveebApp) return;
    if (Array.isArray(form.appIds) && form.appIds.length) return;

    const key = String(dial.oliveebApp).toLowerCase();
    const match = appOptions.find(
      (opt) =>
        String(opt.value).toLowerCase() === key ||
        String(opt.label).toLowerCase() === key
    );

    if (match?.value) {
      setForm((prev) => ({ ...prev, appIds: [match.value] }));
    }
  }, [show, dial?.oliveebApp, form.appIds, appOptions]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "clean"],
    ],
  };

  const handleNameChange = (value) => {
    const raw = value || "";
    const hasSpace = /\s/.test(raw);
    const upper = raw.replace(/\s+/g, "").toUpperCase();
    update("name", upper);

    setNameError(hasSpace ? "Spaces are not allowed in feature names." : "");
  };

  const handleSave = () => {
    if (!form.name?.trim()) {
      setNameError("Feature name is required.");
      toast.error("Feature name required");
      return;
    }
    if (nameError) {
      toast.error("Please fix the feature name.");
      return;
    }

    const selectedApps = Array.isArray(form.appIds) ? form.appIds : [];
    const payload = {
      name: form.name,
      description: form.description,
      isOn: form.isOn,
      appIds: selectedApps,
    };
    onSave({ dialPayload: payload, appIds: selectedApps });
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <Offcanvas
      show={visible}
      onHide={handleClose}
      placement="end"
      backdrop="static"
      style={{
        width: expanded ? "60rem" : "34rem",
        background: "#f6f8fb",
        borderTopLeftRadius: "18px",
        borderBottomLeftRadius: "18px"
      }}
      className="border-0 shadow"
    >
      {/* HEADER */}
      <div
        className="offcanvas-header px-4 py-3 d-flex justify-content-between align-items-center"
        style={{
          borderBottom: "1px solid #eef2f7",
          background: "linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%)"
        }}
      >
        <div>
          <div className="text-900 fw-bold fs-10">{isNew ? "Register Feature Dial" : "Feature Dial"}</div>
          <div className="text-500 fs-11">Manage feature toggle configuration</div>
        </div>

        <div className="d-flex gap-2 align-items-center">
          <ExpandOutlined
            onClick={() => setExpanded((p) => !p)}
            title={expanded ? "Shrink" : "Expand"}
            style={{ cursor: "pointer", fontSize: 16, color: "#111827" }}
          />

          <CloseButton onClick={handleClose} />
        </div>
      </div>

      <div
        className="offcanvas-body px-4 py-3"
        style={{ background: "#f6f8fb" }}
      >
        <style>
          {`
            .feature-dial-quill .ql-container {
              border-radius: 10px;
              background: ${editing ? "#ffffff" : "#f3f4f6"};
              border-color: #e5e7eb;
            }
            .feature-dial-quill .ql-editor {
              min-height: 180px;
              background: ${editing ? "#ffffff" : "#f3f4f6"};
            }
            .feature-dial-quill .ql-toolbar {
              border-color: #e5e7eb;
              border-top-left-radius: 10px;
              border-top-right-radius: 10px;
            }
            .feature-dial-quill .ql-container {
              border-bottom-left-radius: 10px;
              border-bottom-right-radius: 10px;
            }
          `}
        </style>
        {/* HERO FEATURE CARD */}
        <div
          className="p-3 rounded-4 mb-4"
          style={{
            background: "linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)",
            border: "1px solid rgba(59, 130, 246, 0.25)",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
            position: "relative",
            overflow: "hidden"
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 60%)",
              pointerEvents: "none"
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              background: "linear-gradient(180deg, #60a5fa 0%, #a78bfa 100%)"
            }}
          />
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="text-900 fs-10 fw-semibold mb-1">{form.name || "Unnamed Feature"}</div>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <span
                  className={`badge rounded-pill fs-11 ${
                    form.isOn ? "badge-subtle-success" : "badge-subtle-secondary"
                  }`}
                >
                  {form.isOn ? "ACTIVE" : "OFF"}
                </span>

                {Array.isArray(form.appIds) && form.appIds.length > 0 && (
                  <div className="d-flex flex-wrap gap-2">
                    {form.appIds.map((appId) => (
                      <span
                        key={appId}
                        style={{
                          background: "#e0f2fe",
                          color: "#0369a1",
                          borderRadius: "999px",
                          padding: "2px 8px",
                          fontSize: "11px",
                          fontWeight: 500
                        }}
                      >
                        {appLabelById.get(String(appId)) || appId}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Form.Check
              type="switch"
              checked={!!form.isOn}
              disabled={!editing}
              onChange={(e) => update("isOn", e.target.checked)}
            />
          </div>
        </div>

        {/* CONFIGURATION */}
        <div className="mb-4">
          <div className="text-500 fs-11 fw-bold mb-2">CONFIGURATION</div>
          <div className="d-flex flex-column gap-3">
            <div>
              <div className="text-500 fs-11 mb-1">Feature Name</div>
              <Input
                disabled={!editing}
                value={form.name}
                placeholder="ENABLE_BETA_PAYMENTS"
                onChange={(e) => handleNameChange(e.target.value)}
                status={nameError ? "error" : undefined}
              />
              {nameError && (
                <div style={{ marginTop: 4, fontSize: 11, color: "#dc2626" }}>
                  {nameError}
                </div>
              )}
            </div>

            <div>
              <div className="text-500 fs-11 mb-1">Target Apps</div>
              {editing ? (
                <Select
                  mode="multiple"
                  showSearch
                  style={{ width: "100%" }}
                  placeholder="Search and select apps"
                  optionFilterProp="label"
                  options={appOptions}
                  value={form.appIds || []}
                  onChange={(value) => update("appIds", value)}
                  maxTagCount="responsive"
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          background: "#ede9fe",
                          color: "#5b21b6",
                          border: "1px solid #c4b5fd",
                          borderRadius: "999px",
                          padding: "2px 8px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          marginRight: 4,
                          fontSize: "11px"
                        }}
                      >
                        {label}
                        {closable && (
                          <span onClick={onClose} style={{ cursor: "pointer", fontWeight: "bold" }}>×</span>
                        )}
                      </span>
                    );
                  }}
                />
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {(form.appIds || []).length ? (
                    form.appIds.map((appId) => (
                      <span
                        key={appId}
                        style={{
                          background: "#ede9fe",
                          color: "#5b21b6",
                          borderRadius: "999px",
                          padding: "2px 8px",
                          fontSize: "11px"
                        }}
                      >
                        {appLabelById.get(String(appId)) || appId}
                      </span>
                    ))
                  ) : (
                    <span className="text-500 fs-11">---</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DESCRIPTION */}
        <div>
          <div className="text-500 fs-11 fw-bold mb-2">DESCRIPTION</div>
          <div className="feature-dial-quill">
          <ReactQuill
            key={editing ? "feature-dial-desc-edit" : "feature-dial-desc-view"}
            theme="snow"
            readOnly={!editing}
            modules={editing ? quillModules : { toolbar: false }}
            value={form.description || ""}
            onChange={(value) => update("description", value)}
            style={{
              background: editing ? "#ffffff" : "#f3f4f6",
              borderRadius: "10px",
              minHeight: "220px"
            }}
          />
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div
        className="px-4 py-3 d-flex justify-content-between align-items-center"
        style={{
          borderTop: "1px solid #eef2f7",
          background: "#f8fafc"
        }}
      >
        <small className="text-500 fs-11">
          {dial?.id && `ID: ${dial.id}`}
        </small>

        <div className="d-flex gap-2">
          {editing ? (
            <>
              <ButtonAntd
                onClick={() => {
                  setEditing(false);
                  handleClose();
                }}
              >
                Cancel
              </ButtonAntd>

              <ButtonAntd
                type="primary"
                icon={<FontAwesomeIcon icon={faSave} />}
                loading={isSubmitting}
                onClick={handleSave}
              >
                Save
              </ButtonAntd>
            </>
          ) : (
            <ButtonAntd
              type="primary"
              icon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => setEditing(true)}
            >
              Edit
            </ButtonAntd>
          )}
        </div>
      </div>
    </Offcanvas>
  );
}

